import { useState, useEffect, useMemo } from "react";
import { createInternalMessage, generateInternalMessageText, getDepartments } from "../api";
import "./ComposeInternalMessage.css";
import { toast } from "./Toast";

export default function ComposeInternalMessage({ onSent, onCancel }) {
  const [messageType, setMessageType] = useState("meeting");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [meetingDatetime, setMeetingDatetime] = useState("");
  const [duration, setDuration] = useState(30);
  const [location, setLocation] = useState("");
  const [details, setDetails] = useState("");
  const [sendToAll, setSendToAll] = useState(true);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [notifyMethod, setNotifyMethod] = useState("internal");
  const [externalEmails, setExternalEmails] = useState("");

  useEffect(() => {
    getDepartments().then(data => {
      const currentUser = JSON.parse(localStorage.getItem("department") || "{}");
      const filtered = (data.results || data).filter(d => d.id !== currentUser.id && d.is_active !== false);
      setEmployees(filtered);
    }).catch(() => {});
  }, []);

  // Group employees by category
  const grouped = useMemo(() => {
    const groups = {};
    for (const emp of employees) {
      const cat = emp.category_name || "სხვა";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(emp);
    }
    return groups;
  }, [employees]);

  function toggleRecipient(id) {
    setSelectedRecipients(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  }

  function toggleDepartment(categoryName) {
    const deptEmployees = grouped[categoryName] || [];
    const deptIds = deptEmployees.map(e => e.id);
    const allSelected = deptIds.every(id => selectedRecipients.includes(id));
    if (allSelected) {
      // Uncheck all in this department
      setSelectedRecipients(prev => prev.filter(id => !deptIds.includes(id)));
    } else {
      // Check all in this department
      setSelectedRecipients(prev => [...new Set([...prev, ...deptIds])]);
    }
  }

  function isDeptFullySelected(categoryName) {
    const deptEmployees = grouped[categoryName] || [];
    return deptEmployees.length > 0 && deptEmployees.every(e => selectedRecipients.includes(e.id));
  }

  function isDeptPartiallySelected(categoryName) {
    const deptEmployees = grouped[categoryName] || [];
    const some = deptEmployees.some(e => selectedRecipients.includes(e.id));
    const all = deptEmployees.every(e => selectedRecipients.includes(e.id));
    return some && !all;
  }

  async function handleGenerateText() {
    if (!subject) return toast("გთხოვთ, მიუთითეთ თემა.", "warning");
    setGenerating(true);
    try {
      const result = await generateInternalMessageText({
        message_type: messageType,
        subject,
        meeting_datetime: meetingDatetime || null,
        meeting_duration_minutes: duration,
        meeting_location: location,
        details,
      });
      setBody(result.generated_text);
    } catch (err) {
      toast(err.message, "error");
    }
    setGenerating(false);
  }

  async function handleSend() {
    if (!subject) return toast("გთხოვთ, მიუთითეთ თემა.", "warning");
    if (!body) return toast("გთხოვთ, შეავსეთ ტექსტი ან დააგენერირეთ AI-ით.", "warning");
    if (notifyMethod !== "email" && !sendToAll && selectedRecipients.length === 0) return toast("გთხოვთ, აირჩიეთ მინიმუმ ერთი თანამშრომელი.", "warning");
    if ((notifyMethod === "email" || notifyMethod === "both") && messageType === "meeting" && !externalEmails.trim()) return toast("გთხოვთ, მიუთითეთ გარე მეილი.", "warning");
    setSending(true);
    try {
      const payload = {
        message_type: messageType,
        subject,
        body,
        meeting_datetime: meetingDatetime || null,
        meeting_duration_minutes: duration,
        meeting_location: location,
        details,
        send_to_all: sendToAll,
        recipient_ids: sendToAll ? [] : selectedRecipients,
        generate_ai_text: false,
      };
      if (messageType === "meeting") {
        payload.notify_method = notifyMethod;
        if (notifyMethod === "email" || notifyMethod === "both") {
          payload.external_emails = externalEmails.split(",").map(e => e.trim()).filter(Boolean);
        }
      }
      await createInternalMessage(payload);
      onSent();
    } catch (err) {
      toast(err.message, "error");
    }
    setSending(false);
  }

  return (
    <div className="cim-container">
      <div className="cim-header">
        <h3>ახალი შეტყობინება</h3>
        <button className="cim-close-btn" onClick={onCancel}>&times;</button>
      </div>

      <div className="cim-type-toggle">
        <button
          className={`cim-type-btn ${messageType === "meeting" ? "active" : ""}`}
          onClick={() => setMessageType("meeting")}
        >
          შეხვედრა
        </button>
        <button
          className={`cim-type-btn ${messageType === "announcement" ? "active" : ""}`}
          onClick={() => setMessageType("announcement")}
        >
          შეტყობინება
        </button>
      </div>

      <div className="cim-fields">
        <div className="cim-field">
          <label>თემა</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="შეტყობინების თემა"
          />
        </div>

        {messageType === "meeting" && (
          <>
            <div className="cim-row">
              <div className="cim-field">
                <label>თარიღი და დრო</label>
                <input
                  type="datetime-local"
                  value={meetingDatetime}
                  onChange={(e) => setMeetingDatetime(e.target.value)}
                />
              </div>
              <div className="cim-field" style={{ maxWidth: 120 }}>
                <label>ხანგრძლივობა</label>
                <input
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  min={5}
                />
              </div>
            </div>
            <div className="cim-field">
              <label>ადგილი</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="შეხვედრის ოთახი / Zoom ბმული"
              />
            </div>

            <div className="cim-field">
              <label>შეტყობინების გაგზავნის მეთოდი</label>
              <div className="cim-notify-toggle">
                <button
                  className={`cim-type-btn ${notifyMethod === "internal" ? "active" : ""}`}
                  onClick={() => setNotifyMethod("internal")}
                  type="button"
                >
                  შიდა სისტემა
                </button>
                <button
                  className={`cim-type-btn ${notifyMethod === "email" ? "active" : ""}`}
                  onClick={() => setNotifyMethod("email")}
                  type="button"
                >
                  მეილი
                </button>
                <button
                  className={`cim-type-btn ${notifyMethod === "both" ? "active" : ""}`}
                  onClick={() => setNotifyMethod("both")}
                  type="button"
                >
                  ორივე
                </button>
              </div>
            </div>

            {(notifyMethod === "email" || notifyMethod === "both") && (
              <div className="cim-field">
                <label>გარე მეილი (მძიმით გამოყოფილი)</label>
                <input
                  type="text"
                  value={externalEmails}
                  onChange={(e) => setExternalEmails(e.target.value)}
                  placeholder="example@mail.com, other@mail.com"
                />
              </div>
            )}
          </>
        )}

        <div className="cim-field">
          <label>დამატებითი დეტალები (AI-სთვის)</label>
          <input
            type="text"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="რაზეა შეხვედრა / რა თემას ეხება"
          />
        </div>

        <div className="cim-field">
          <div className="cim-body-header">
            <label>ტექსტი</label>
            <button
              className="cim-ai-btn"
              onClick={handleGenerateText}
              disabled={generating}
            >
              {generating ? "გენერირდება..." : "AI-ით გენერირება"}
            </button>
          </div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            placeholder="შეტყობინების ტექსტი..."
          />
        </div>

        <div className="cim-option">
          <label>
            <input
              type="checkbox"
              checked={sendToAll}
              onChange={(e) => {
                setSendToAll(e.target.checked);
                if (e.target.checked) setSelectedRecipients([]);
              }}
            />
            ყველა თანამშრომლისთვის გაგზავნა
          </label>
        </div>

        {!sendToAll && (
          <div className="cim-recipients">
            <label className="cim-recipients-label">აირჩიეთ მიმღებები:</label>
            <div className="cim-recipients-list">
              {Object.entries(grouped).map(([catName, members]) => (
                <div key={catName} className="cim-dept-group">
                  <label className="cim-dept-header">
                    <input
                      type="checkbox"
                      checked={isDeptFullySelected(catName)}
                      ref={el => {
                        if (el) el.indeterminate = isDeptPartiallySelected(catName);
                      }}
                      onChange={() => toggleDepartment(catName)}
                    />
                    <span className="cim-dept-name">{catName}</span>
                    <span className="cim-dept-count">{members.length}</span>
                  </label>
                  <div className="cim-dept-members">
                    {members.map(emp => (
                      <label key={emp.id} className="cim-recipient-item">
                        <input
                          type="checkbox"
                          checked={selectedRecipients.includes(emp.id)}
                          onChange={() => toggleRecipient(emp.id)}
                        />
                        <span className="cim-recipient-name">{emp.department_name}</span>
                        <span className="cim-recipient-email">{emp.email}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              {employees.length === 0 && (
                <span className="cim-no-employees">თანამშრომლები ვერ მოიძებნა</span>
              )}
            </div>
            {selectedRecipients.length > 0 && (
              <div className="cim-selected-count">
                არჩეულია: {selectedRecipients.length} თანამშრომელი
              </div>
            )}
          </div>
        )}
      </div>

      <div className="cim-actions">
        <button className="cim-cancel-btn" onClick={onCancel}>გაუქმება</button>
        <button className="cim-send-btn" onClick={handleSend} disabled={sending}>
          {sending ? "იგზავნება..." : "გაგზავნა"}
        </button>
      </div>
    </div>
  );
}
